import { Card, CardTitle, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui'
import { StatusPill } from './StatusPill'
import { useTranslations } from '@/lib/i18n'
import { formatMoney, formatDateTime } from '@/lib/format'
import type { ActivityRow, StatusFamily } from '@/types'

const ACTION_STATUS_FAMILY: Record<string, StatusFamily> = { succeeded: 'success', failed: 'failed' }

/**
 * The action history for a single transaction (refunds/captures/reversals we
 * performed on it). Shown on payment/payout detail to anyone who can see the
 * transaction. Renders nothing when no action has been taken yet.
 */
export function TransactionActivity({ items }: { items: ActivityRow[] }) {
  const { t, locale } = useTranslations()
  if (items.length === 0) return null

  return (
    <Card>
      <CardTitle>{t('activity.transaction_title')}</CardTitle>
      <Table className="mt-2">
        <TableHead>
          <TableRow>
            <TableHeader>{t('activity.columns.date')}</TableHeader>
            <TableHeader>{t('activity.columns.actor')}</TableHeader>
            <TableHeader>{t('activity.columns.action')}</TableHeader>
            <TableHeader className="text-right">{t('activity.columns.amount')}</TableHeader>
            <TableHeader>{t('activity.columns.status')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="whitespace-nowrap">{formatDateTime(row.created_at, locale)}</TableCell>
              <TableCell>{row.actor ?? '—'}</TableCell>
              <TableCell>{t(`activity.actions.${row.action}`)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {row.amount && row.currency ? formatMoney(row.amount, row.currency, locale) : '—'}
              </TableCell>
              <TableCell>
                {row.status ? (
                  <StatusPill family={ACTION_STATUS_FAMILY[row.status] ?? 'neutral'}>
                    {t(`activity.statuses.${row.status}`)}
                  </StatusPill>
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}
